/**
 * Perspective Grid (VC Shift+P) + helpers de movimiento en plano.
 * Coords normalizadas 0–1 sobre el artboard.
 */

export function defaultPerspectiveGrid(overrides = {}) {
  return {
    mode: "2point", // 1point | 2point
    visible: true,
    origin: { x: 0.5, y: 0.78 },
    vpLeft: { x: 0.06, y: 0.36 },
    vpRight: { x: 0.94, y: 0.36 },
    cells: 6,
    plane: "ground", // left | right | ground | none
    ...overrides,
  };
}

export function normalizePerspectiveGrid(raw) {
  if (!raw || typeof raw !== "object") return null;
  const base = defaultPerspectiveGrid();
  const mode =
    String(raw.mode || base.mode).toLowerCase() === "1point"
      ? "1point"
      : "2point";
  const planeRaw = String(raw.plane || base.plane).toLowerCase();
  const plane = ["left", "right", "ground", "none"].includes(planeRaw)
    ? planeRaw
    : "ground";
  const numPt = (p, fb) => ({
    x: Number.isFinite(Number(p?.x)) ? Number(p.x) : fb.x,
    y: Number.isFinite(Number(p?.y)) ? Number(p.y) : fb.y,
  });
  const cells = Math.max(
    2,
    Math.min(16, Math.round(Number(raw.cells) || base.cells)),
  );
  return {
    mode,
    visible: raw.visible !== false,
    origin: numPt(raw.origin, base.origin),
    vpLeft: numPt(raw.vpLeft, base.vpLeft),
    vpRight: numPt(raw.vpRight, mode === "1point" ? base.vpLeft : base.vpRight),
    cells,
    plane,
  };
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function line(a, b, plane) {
  return {
    x1: a.x,
    y1: a.y,
    x2: b.x,
    y2: b.y,
    plane,
  };
}

/**
 * Segmentos SVG de la rejilla (left azul, right naranja, ground verde).
 */
export function perspectiveGridSegments(grid) {
  const g = normalizePerspectiveGrid(grid);
  if (!g || !g.visible) return [];
  const { origin: O, vpLeft: VL, vpRight: VR, cells, mode } = g;
  const segs = [];
  const n = cells;

  // Horizontales de suelo (entre rayos L/R)
  for (let i = 1; i <= n; i += 1) {
    const t = i / (n + 1);
    if (mode === "1point") {
      const vp = VL;
      const left = {
        x: lerp(O.x - 0.45, vp.x, t * 0.85),
        y: lerp(O.y, vp.y, t * 0.85),
      };
      const right = {
        x: lerp(O.x + 0.45, vp.x, t * 0.85),
        y: lerp(O.y, vp.y, t * 0.85),
      };
      segs.push(line(left, right, "ground"));
    } else {
      const a = {
        x: lerp(O.x, VL.x, t),
        y: lerp(O.y, VL.y, t),
      };
      const b = {
        x: lerp(O.x, VR.x, t),
        y: lerp(O.y, VR.y, t),
      };
      segs.push(line(a, b, "ground"));
    }
  }

  // Rayos hacia VP izquierdo (plano left + suelo)
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const foot = {
      x: lerp(O.x - 0.42, O.x + (mode === "1point" ? 0.42 : 0), t),
      y: O.y,
    };
    if (mode === "2point") {
      foot.x = lerp(O.x - 0.4, O.x, t);
    }
    segs.push(line(foot, VL, i === Math.floor(n / 2) ? "left" : "ground"));
  }

  // Rayos hacia VP derecho
  if (mode === "2point") {
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const foot = {
        x: lerp(O.x, O.x + 0.4, t),
        y: O.y,
      };
      segs.push(line(foot, VR, i === Math.floor(n / 2) ? "right" : "ground"));
    }
  }

  // Verticales (frente / muro) desde suelo hacia arriba
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const x = lerp(O.x - 0.4, O.x + 0.4, t);
    segs.push(
      line({ x, y: O.y }, { x, y: Math.min(O.y, VL.y) - 0.28 }, "left"),
    );
  }

  // Horizonte
  segs.push(
    line(
      { x: -0.05, y: VL.y },
      { x: 1.05, y: mode === "1point" ? VL.y : VR.y },
      "none",
    ),
  );

  return segs;
}

export function perspectivePlaneColor(plane) {
  switch (plane) {
    case "left":
      return "#3366ff";
    case "right":
      return "#ff8c1a";
    case "ground":
      return "#2eb84a";
    default:
      return "#8899aa";
  }
}

function unit(dx, dy) {
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

/**
 * Proyecta (dx,dy) sobre el plano activo en el punto (cx,cy).
 * left/right → 1 eje hacia el VP; ground → plano 2D L+R; none → libre.
 */
export function projectDeltaOnPerspectivePlane(dx, dy, cx, cy, grid) {
  const g = normalizePerspectiveGrid(grid);
  if (!g || g.plane === "none") {
    return { dx: Number(dx) || 0, dy: Number(dy) || 0 };
  }
  const ox = Number(dx) || 0;
  const oy = Number(dy) || 0;
  if (g.plane === "left") {
    const u = unit(g.vpLeft.x - cx, g.vpLeft.y - cy);
    const mag = ox * u.x + oy * u.y;
    return { dx: u.x * mag, dy: u.y * mag };
  }
  if (g.plane === "right") {
    const vp = g.mode === "1point" ? g.vpLeft : g.vpRight;
    const u = unit(vp.x - cx, vp.y - cy);
    const mag = ox * u.x + oy * u.y;
    return { dx: u.x * mag, dy: u.y * mag };
  }
  // ground: combinación de dirección a VP-L y VP-R
  const uL = unit(g.vpLeft.x - cx, g.vpLeft.y - cy);
  const vpR = g.mode === "1point" ? g.vpLeft : g.vpRight;
  const uR = unit(vpR.x - cx, vpR.y - cy);
  // base ortonormal aproximada en el plano pantalla
  const a = ox * uL.x + oy * uL.y;
  const b = ox * uR.x + oy * uR.y;
  return {
    dx: uL.x * a + uR.x * b,
    dy: uL.y * a + uR.y * b,
  };
}
